import { useEffect, useState } from "react";
import { Table, Card, Button, Input, Space, Modal, Descriptions, Tag } from "antd";
import { SearchOutlined, EyeOutlined } from "@ant-design/icons";
import { getAllUsers, updateUserStatus } from "../../services/UserServices"; // Importa el nuevo servicio
import { useAuth } from "../../contexts/authContext/index";

const UsersPage = () => {
    const [usersData, setUsersData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDetailVisible, setUserDetailVisible] = useState(false);
    const { getToken } = useAuth();

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const token = await getToken();
                const users = await getAllUsers(token);
                setUsersData(users);
            } catch (error) {
                console.error("Error fetching users:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [getToken]);

    // Filter data based on search text
    const filteredData = usersData.filter((user) => {
        if (searchText === "") return true;

        const searchValue = searchText.toLowerCase();

        // Search in all relevant fields
        return (
            (user.name && user.name.toLowerCase().includes(searchValue)) ||
            (user.cuit && user.cuit.toLowerCase().includes(searchValue)) ||
            (user.email && user.email.toLowerCase().includes(searchValue)) ||
            (user.role && user.role.toLowerCase().includes(searchValue)) ||
            (user.company && user.company.toLowerCase().includes(searchValue))
        );
    });

    const handleSearch = (e) => {
        setSearchText(e.target.value);
    };

    const handleViewDetails = (user) => {
        setSelectedUser(user);
        setUserDetailVisible(true);
    };

    const handleToggleActive = async (user) => {
        try {
            const token = await getToken();
            await updateUserStatus(user.uid, token); // Llama al servicio para actualizar el estado del usuario
            // Actualiza el estado local tras la activación/desactivación
            const updatedUsers = usersData.map(u => {
                if (u.uid === user.uid) {
                    return { ...u, active: !u.active };
                }
                return u;
            });
            setUsersData(updatedUsers);
        } catch (error) {
            console.error("Error toggling user active status:", error);
        }
    };

    const columns = [
        {
            title: "Nombre",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: "CUIT",
            dataIndex: "cuit",
            key: "cuit",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Rol",
            dataIndex: "role",
            key: "role",
            filters: [
                { text: "Admin", value: "admin" },
                { text: "Cliente", value: "cliente" },
                { text: "Proveedor", value: "vendedor" },
            ],
            onFilter: (value, record) => record.role.indexOf(value) === 0,
            render: (role) => {
                let color = 'blue';
                if (role === 'admin') color = 'red';
                if (role === 'vendedor') color = 'green';
                return <Tag color={color}>{role.toUpperCase()}</Tag>;
            }
        },
        {
            title: "Estado",
            dataIndex: "active",
            key: "active",
            render: (active) => (
                <span style={{ color: active ? "green" : "red" }}>
                    {active ? "Activo" : "Inactivo"}
                </span>
            ),
        },
        {
            title: "Empresa",
            dataIndex: "company",
            key: "company",
        },
        {
            title: "Acciones",
            key: "actions",
            render: (_, record) => (
                <Space size="small">
                    <Button 
                        type={record.active ? "default" : "primary"} 
                        size="small"
                        onClick={() => handleToggleActive(record)}
                    >
                        {record.active ? "Desactivar" : "Activar"}
                    </Button>
                    <Button 
                        type="link" 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetails(record)}
                    >
                        Ver +
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Card
                title="Gestión de Usuarios"
                extra={
                    <Space>
                        <Input 
                            placeholder="Buscar usuarios" 
                            prefix={<SearchOutlined />} 
                            style={{ width: 200 }} 
                            value={searchText}
                            onChange={handleSearch}
                            allowClear
                        />
                    </Space>
                }
            >
                <Table
                    dataSource={filteredData}
                    columns={columns}
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    rowKey="uid"
                />
            </Card>

            {/* Modal para mostrar detalles del usuario */}
            <Modal
                title={`Detalles del Usuario: ${selectedUser?.name || ''}`}
                open={userDetailVisible}
                onCancel={() => setUserDetailVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setUserDetailVisible(false)}>
                        Cerrar
                    </Button>
                ]}
                width={700}
            >
                {selectedUser && (
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label="Nombre" span={2}>{selectedUser.name || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Email">{selectedUser.email || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="CUIT">{selectedUser.cuit || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Empresa">{selectedUser.company || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Rol">
                            <Tag color={selectedUser.role === 'admin' ? 'red' : selectedUser.role === 'vendedor' ? 'green' : 'blue'}>
                                {selectedUser.role?.toUpperCase() || 'N/A'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Estado">
                            <Tag color={selectedUser.active ? 'green' : 'red'}>
                                {selectedUser.active ? 'ACTIVO' : 'INACTIVO'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Teléfono" span={2}>{selectedUser.phone || 'N/A'}</Descriptions.Item>
                    </Descriptions> 
                )}
            </Modal>
        </>
    );
};

export default UsersPage;
